import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@utils/api";

import { dashboardQKeys } from "@hooks/queries/dashboard/queryKeys";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";


export interface CommissionReconciliationPreview {
  reconciliation_id: string;
  expires_at: string;
  staff_id: string;
  staff_name: string;
  partner_id: string;
  partner_name: string;
  year: number;
  month: number;
  payments_analyzed: number;
  payments_requiring_changes: number;
  unchanged_payments: number;
  payments_without_contract: number;
  blocked_payments: number;
  can_apply: boolean;
  old_provider_total: number;
  new_provider_total: number;
  provider_difference: number;
  old_platform_total: number;
  new_platform_total: number;
  platform_difference: number;
  source_counts: Record<string, number>;
}

export const useCommissionReconciliationPartners = (
  staffId: string | null,
  enabled: boolean
) =>
  useQuery({
    queryKey: ["commission-reconciliation-partners", staffId],
    queryFn: () =>
      api
        .get<{ id: string; name: string }[]>(
          "earnings/admin/commission-reconciliation/partners",
          { params: { staff_id: staffId } }
        )
        .then(response => response.data),
    enabled: enabled && !!staffId,
  });

export const usePreviewCommissionReconciliation = () => {
  const handleErrors = useQueryErrorHandling();

  return useMutation({
    mutationFn: (payload: {
      staff_id: string;
      year: number;
      month: number;
      partner_id?: string;
    }) =>
      api
        .post<CommissionReconciliationPreview>(
          "earnings/admin/commission-reconciliation/preview",
          payload
        )
        .then(response => response.data),
    onError: (error: any) => handleErrors(error, "mutation"),
  });
};

export const useApplyCommissionReconciliation = () => {
  const handleErrors = useQueryErrorHandling();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { reconciliation_id: string; reason: string }) =>
      api
        .post("earnings/admin/commission-reconciliation/apply", payload)
        .then(response => response.data),
    onSuccess: () => {
      notifications.show({
        title: "Commission reconciliation completed",
        message: "The earnings and payout figures have been refreshed.",
        color: "green",
      });
      queryClient.invalidateQueries({ queryKey: dashboardQKeys.all() });
    },
    onError: (error: any) => handleErrors(error, "mutation"),
  });
};
